import React, { memo, useEffect, useMemo, useRef } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { FormikPartnerSelect } from 'src/components/payments-ops/FormikPartnerSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import type { BankOnboardingFormValues } from 'src/interfaces/bankOnboarding'
import { getCustomers } from 'src/selectors/customer'
import { resolveCustomerDisplayName } from 'src/utils/customerList'

interface Props {
  blockedPartnerReasons: Record<string, string>
}

const PartnerStepComponent = ({ blockedPartnerReasons }: Props) => {
  const customers = useSelector(getCustomers)
  const { values, setFieldValue } = useFormikContext<BankOnboardingFormValues>()

  const selectedPartner = useMemo(() => {
    return (
      customers.find((customer) => {
        return customer.id === values.partnerId
      }) ?? null
    )
  }, [customers, values.partnerId])

  // The last nickname this step wrote (or found already equal to the partner's name). Only a
  // blank nickname or one still equal to it is overwritten — a typed nickname is never touched.
  const lastAutoFilledRef = useRef<string | null>(null)

  // The virtual account nickname follows the partner's name (Retool: "partner.name").
  useEffect(() => {
    if (!selectedPartner) {
      return
    }

    const displayName = resolveCustomerDisplayName(selectedPartner)
    const nickname = values.virtualAccountNickName

    if (nickname === displayName) {
      lastAutoFilledRef.current = displayName

      return
    }

    const isBlank = nickname.trim() === ''
    const isStillAutoFilled =
      lastAutoFilledRef.current !== null && nickname === lastAutoFilledRef.current

    if (!isBlank && !isStillAutoFilled) {
      return
    }

    lastAutoFilledRef.current = displayName
    setFieldValue('virtualAccountNickName', displayName)
  }, [selectedPartner, setFieldValue, values.virtualAccountNickName])

  return (
    <div css={tw`flex flex-col gap-4`}>
      <FormikPartnerSelect<BankOnboardingFormValues>
        name="partnerId"
        label="Partner *"
        blockedPartnerReasons={blockedPartnerReasons}
        dataTestId="onboarding-partner"
      />
      <FormikTextField<BankOnboardingFormValues>
        name="virtualAccountNickName"
        label="Virtual account nickname *"
        placeholder="Defaults to the partner name"
      />
      <Typography variant="h5" css={tw`mt-2`}>
        Program manager (GigSafe)
      </Typography>
      <div css={tw`grid grid-cols-2 gap-4`}>
        <FormikTextField<BankOnboardingFormValues>
          name="programManagerIdentificationNumber"
          label="GigSafe EIN *"
          placeholder="9 digits"
        />
        <FormikTextField<BankOnboardingFormValues>
          name="programManagerName"
          label="Program manager name *"
          placeholder="Program manager name"
        />
        <FormikTextField<BankOnboardingFormValues>
          name="contactName"
          label="Contact name *"
          placeholder="Contact name"
        />
        <FormikTextField<BankOnboardingFormValues>
          name="contactEmailAddress"
          label="Contact email *"
          placeholder="name@gigsafe.com"
          type="email"
        />
      </div>
    </div>
  )
}

export const PartnerStep = memo(PartnerStepComponent)
